import './style.css';
import './lib/i18n';
import { bootstrapAuth } from './lib/authBootstrap';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { queryClient } from './lib/queryClient';
import { router } from './router';
import { SystemConfigBootstrap } from './components/SystemConfigBootstrap';
import { PortalAvailabilityGate } from './components/common/PortalAvailabilityGate';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// DeepHealth Design Language System (DLS) v3.0 — brand guidelines v1.2 (Oct 2024)
// Full spec: docs/dh-theme/SKILL.md
const adminTheme = createTheme({
  palette: {
    primary:   { main: '#a10693', dark: '#61045a', light: '#f95aec' },
    error:     { main: '#ff071c', light: '#fff0f1' },
    warning:   { main: '#ff5900', light: '#fff3eb' },
    success:   { main: '#24c246', light: '#edf8ef' },
    info:      { main: '#187ae2', light: '#eaf2fd' },
    text: {
      primary:   'rgba(0,0,0,0.87)',
      secondary: 'rgba(0,0,0,0.6)',
      disabled:  'rgba(0,0,0,0.38)',
    },
    divider: 'rgba(0,0,0,0.12)',
    grey: {
      50: '#fafafa', 100: '#f5f5f5', 200: '#ebebeb', 300: '#e0e0e0',
      400: '#bdbdbd', 500: '#9e9e9e', 600: '#757575', 700: '#616161',
      800: '#424242', 900: '#212121',
    },
    background: { default: '#f5f5f5', paper: '#ffffff' },
  },
  // DLS mandates 4px radius everywhere (not shadcn's 6–8px).
  shape: { borderRadius: 4 },
  typography: {
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    // DH overrides: body weight 300, headings/subtitles weight 500
    body1:     { fontWeight: 300, lineHeight: 1.55 },
    body2:     { fontWeight: 300, lineHeight: 1.43 },
    h3:        { fontWeight: 500, lineHeight: 1.35 },
    h4:        { fontWeight: 500, lineHeight: 1.35 },
    h5:        { fontWeight: 500, lineHeight: 1.35 },
    h6:        { fontWeight: 500, lineHeight: 1.35, letterSpacing: '0.0075em' },
    subtitle1: { fontWeight: 500, lineHeight: 1.55 },
    subtitle2: { fontWeight: 500, lineHeight: 1.57 },
    overline:  { fontWeight: 500, lineHeight: 2.66 },
    button:    { fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.02857em' },
  },
  components: {
    MuiCssBaseline: { styleOverrides: { body: false } },
    MuiButton: {
      styleOverrides: {
        // Medium = 36px (DLS), not MUI default 36px — already correct.
        // Contained gets elevation 2 at rest, 4 on hover.
        contained: {
          boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
          '&:hover': {
            boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
          },
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 1 },
    },
  },
});

bootstrapAuth();

const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

createRoot(root).render(
  <StrictMode>
    <ThemeProvider theme={adminTheme}>
      <CssBaseline enableColorScheme />
      <QueryClientProvider client={queryClient}>
        <SystemConfigBootstrap />
        <PortalAvailabilityGate>
          <RouterProvider router={router} />
        </PortalAvailabilityGate>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>
);
